import React, { useState, useRef, ChangeEvent } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Identifier, XYCoord } from 'dnd-core';
import { FormControl, InputGroup, Badge } from 'react-bootstrap';
import classNames from 'classnames';
import * as yup from 'yup';
import { ReactComponent as EditIcon } from '../../../shared/icons/edit.svg';
import { ReactComponent as DragIcon } from '../../../shared/icons/drag.svg';
import { ReactComponent as ApproveIcon } from '../../../shared/icons/checkmark-icon.cc.svg';
import { ReactComponent as RejectIcon } from '../../../shared/icons/reject.svg';
import { EMAIL_VALIDATION_REGEX } from '../../../shared/constants/emailValidation';
import styles from './UpdateSignerItem.module.scss';

interface UpdateSignerItemProps {
  id: string;
  order: number;
  email: string;
  index: number;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  updateSigner: (id: string, email: string) => void;
  canEdit: boolean;
  statusBadge: string;
  isDraggable: boolean;
  showOrderNumber: boolean;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const emailValidationSchema = yup.string().required().matches(EMAIL_VALIDATION_REGEX, 'Please provide correct email');

export const UpdateSignerItem: React.FC<UpdateSignerItemProps> = ({
  id,
  index,
  email,
  order,
  moveCard,
  updateSigner,
  canEdit,
  statusBadge,
  isDraggable,
  showOrderNumber,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [emailValue, setEmailValue] = useState(email);
  const [isEmailValid, setIsEmailValid] = useState<boolean | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLSpanElement>(null);

  // Drag and drop setup
  const [, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: 'signer',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current || !isDraggable) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveCard(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'signer',
    item: () => {
      return { id, email, index, order };
    },
    collect: (monitor) => {
      return { isDragging: monitor.isDragging() };
    },
    canDrag: isDraggable && !isEditing,
  });

  // Drag by handle only; a fully draggable row breaks input taps on mobile
  if (isDraggable) {
    drop(ref);
    drag(dragHandleRef);
  }

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmailValue(newEmail);

    try {
      emailValidationSchema.validateSync(newEmail);
      setIsEmailValid(true);
    } catch {
      setIsEmailValid(false);
    }
  };

  const handleSaveEmail = () => {
    if (isEmailValid && emailValue !== email) {
      updateSigner(id, emailValue);
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEmailValue(email);
    setIsEmailValid(null);
    setIsEditing(false);
  };

  const getStatusBadgeVariant = () => {
    if (statusBadge.includes('Signed')) return 'success';
    if (statusBadge.includes('Modified')) return 'warning';
    return 'secondary';
  };

  return (
    <div
      className={classNames(styles.signerItem, {
        [styles.dragging]: isDragging,
        [styles.disabled]: !canEdit,
      })}
      ref={ref}
    >
      <div className={styles.content}>
        {showOrderNumber && <span className={styles.orderNumber}>{order + 1}</span>}

        {isDraggable && (
          <span className={styles.dragHandle} ref={dragHandleRef}>
            <DragIcon className={styles.dragIcon} />
          </span>
        )}

        <div className={styles.emailSection}>
          {!isEditing ? (
            <>
              <span className={styles.email}>{email}</span>
              <div className={styles.rightSection}>
                <Badge variant={getStatusBadgeVariant()} className={styles.statusBadge}>
                  {statusBadge}
                </Badge>
                {canEdit && (
                  <button className={styles.editButton} onClick={() => setIsEditing(true)} aria-label="Edit email">
                    <EditIcon className={styles.editIcon} />
                  </button>
                )}
              </div>
            </>
          ) : (
            <InputGroup className={styles.editGroup}>
              <FormControl
                value={emailValue}
                onChange={handleEmailChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && isEmailValid) {
                    handleSaveEmail();
                  } else if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
                isInvalid={isEmailValid === false}
                autoFocus
                placeholder="Enter email"
              />
              <button
                className={styles.saveButton}
                onClick={handleSaveEmail}
                disabled={!isEmailValid}
                aria-label="Save"
              >
                <ApproveIcon className={styles.approveIcon} />
              </button>
              <button className={styles.cancelButton} onClick={handleCancelEdit} aria-label="Cancel">
                <RejectIcon className={styles.rejectIcon} />
              </button>
            </InputGroup>
          )}
        </div>
      </div>
    </div>
  );
};
